import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  IconButton, 
  Button,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Chip,
  Stack
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  ThumbUp,
  ThumbUpOutlined,
  Share,
  Comment,
  Visibility,
  Send,
  CameraAlt
} from '@mui/icons-material';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import VideoApiService from '../../services/video-api-service';
import VideoComments from './VideoComments';
import ApiService from '../../services/api-service';
import { getUserId } from '../../constants/function';
import './VideoPlayer.css';

const VideoPlayer = () => {
  const { videoId } = useParams();
  const history = useHistory();
  const videoRef = useRef(null);
  const viewCountedRef = useRef(false);

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [showComments, setShowComments] = useState(true);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [savingThumbnail, setSavingThumbnail] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  useEffect(() => {
    // Reset states when video changes
    setPlaying(false);
    setLiked(false);
    viewCountedRef.current = false;
    
    // Fetch new video data
    fetchVideoDetails();
    fetchRelatedVideos();
    
    // Cleanup function to pause video when unmounting
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, [videoId]);

  // Track view when video data is loaded
  useEffect(() => {
    if (video && !viewCountedRef.current) {
      console.log('[VideoPlayer] Starting 3-second view tracking timer...');
      // Track view after 3 seconds
      const timer = setTimeout(() => {
        console.log('[VideoPlayer] 3 seconds elapsed, calling trackView()');
        trackView();
      }, 3000);
      
      return () => {
        console.log('[VideoPlayer] Cleanup: clearing view tracking timer');
        clearTimeout(timer);
      };
    }
  }, [video]);

  const fetchVideoDetails = async () => {
    try {
      setLoading(true);
      const response = await VideoApiService.getVideo(videoId);
      if (response.success) {
        const videoData = response.result;
        const video = Array.isArray(videoData) ? videoData[0] : videoData;
        
        setVideo(video);
        setLikeCount(video.likes || 0);
        setViewCount(video.views || 0);
        setLiked(video.isLiked || false);
        
        // Check if current user is the owner
        const currentUserId = getUserId();
        
        const uploaderIds = [
          video.uploader?._id,
          video.uploader?.id,
          video.uploader, // Direct string ID
          video.createdBy,
          video.uploadedBy,
          video.studioId
        ].filter(Boolean);
        
        console.log('Ownership Debug:', {
          currentUserId,
          uploaderIds,
          uploader: video.uploader,
          video: video
        });
        
        const isVideoOwner = currentUserId && uploaderIds.some(id => 
          String(currentUserId) === String(id)
        );
        
        console.log('Is video owner:', isVideoOwner);
        setIsOwner(isVideoOwner);
        
        // Track view after 3 seconds (moved to useEffect to ensure video data is available)
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      console.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedVideos = async () => {
    try {
      const response = await VideoApiService.getRelatedVideos(videoId, 10);
      if (response.success) {
        setRelatedVideos(response.result);
      }
    } catch (error) {
      console.error('Error fetching related videos:', error);
    }
  };

  const trackView = async () => {
    console.log('[trackView] Called - viewCountedRef:', viewCountedRef.current, 'video:', !!video);
    
    if (viewCountedRef.current || !video) {
      console.log('[trackView] Skipping - already counted or no video data');
      return;
    }
    
    try {
      // Get studioId from video data
      const studioId = video.studioId || video.uploader?._id || video.creator?._id;
      console.log('[trackView] Found studioId:', studioId, 'from video:', {
        studioId: video.studioId,
        uploaderId: video.uploader?._id,
        creatorId: video.creator?._id
      });
      
      if (!studioId) {
        console.error('[trackView] Cannot track view: No studioId found');
        return;
      }
      
      console.log('[trackView] Calling API - videoId:', videoId, 'studioId:', studioId);
      const response = await VideoApiService.trackView(videoId, studioId);
      console.log('[trackView] API Response:', response);
      
      if (response.success || response.ok) {
        viewCountedRef.current = true;
        const newCount = viewCount + 1;
        setViewCount(newCount);
        console.log('[trackView] View tracked successfully! New count:', newCount);
      } else {
        console.error('[trackView] API returned unsuccessful response:', response);
      }
    } catch (error) {
      console.error('[trackView] Error tracking view:', error.response || error);
    }
  };

  const handleLike = async () => {
    try {
      const response = liked 
        ? await VideoApiService.unlikeVideo(videoId)
        : await VideoApiService.likeVideo(videoId);
      
      if (response.success) {
        setLiked(!liked);
        setLikeCount(prev => liked ? prev - 1 : prev + 1);
        console.log(liked ? 'Removed from likes' : 'Added to likes');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      console.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/video/${videoId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: video.title,
          text: video.description,
          url: shareUrl
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard');
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const handleVideoEnded = () => {
    // Auto-play next video if available
    if (relatedVideos.length > 0) {
      const nextVideo = relatedVideos[0];
      history.push(`/video/${nextVideo._id}`);
    }
  };

  const handleVideoLoaded = () => {
    // Auto-play when video is loaded (for smooth transitions)
    if (videoRef.current) {
      // Small delay to ensure video is ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play()
            .then(() => {
              setPlaying(true);
              setBuffering(false);
            })
            .catch(err => {
              console.log('Autoplay prevented:', err);
              setPlaying(false);
              setBuffering(false);
            });
        }
      }, 100);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const captureCurrentFrame = async () => {
    if (!videoRef.current || !isOwner) return;
    
    setSavingThumbnail(true);
    
    try {
      const videoElement = videoRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      
      // Draw current video frame to canvas
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      // Create FormData and send to API
      const formData = new FormData();
      formData.append('thumbnailImg', blob, 'thumbnail.jpg');
      
      // Extract studioId from video data object
      const studioId = video?.studioId || video?.uploader?._id;
      
      if (!studioId) {
        console.error('Cannot determine studio ID');
        return;
      }
      
      // Update video thumbnail using existing API
      const response = await ApiService.UPDATEVIDEO({
        studioId: studioId,
        videoId: videoId,
        formData: formData
      });
      
      if (response.success) {
        console.log('Thumbnail updated successfully');
        // Optionally refresh video data to show new thumbnail
        setTimeout(() => {
          fetchVideoDetails();
        }, 1000);
      } else {
        console.error('Failed to update thumbnail');
      }
      
    } catch (error) {
      console.error('Error capturing frame:', error);
      console.error('Failed to save thumbnail');
    } finally {
      setSavingThumbnail(false);
    }
  };

  const formatViewCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!video) {
    return (
      <Container>
        <Typography variant="h5" align="center" sx={{ mt: 4 }}>
          Video not found
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'var(--background-color, #0a0a0a)', minHeight: '100vh', color: 'var(--text-primary, #fff)' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Video Player Section */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ bgcolor: 'var(--background-color, #000)', position: 'relative' }}>
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <video
                  ref={videoRef}
                  src={video.videoUrl}
                  poster={video.thumbnailUrl}
                  preload="metadata"
                  controlsList="nodownload"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onClick={handlePlayPause}
                  onEnded={handleVideoEnded}
                  onLoadedMetadata={handleVideoLoaded}
                  onWaiting={() => setBuffering(true)}
                  onPlaying={() => setBuffering(false)}
                  onTimeUpdate={(e) => {
                    setVideoProgress(e.target.currentTime);
                    setVideoDuration(e.target.duration);
                  }}
                  onCanPlay={() => setBuffering(false)}
                />
                
                {/* Loading/Buffering Overlay */}
                {(loading || buffering) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.7)',
                      pointerEvents: buffering ? 'none' : 'auto'
                    }}
                  >
                    <CircularProgress sx={{ color: 'var(--primary-color, #ffee20)' }} />
                  </Box>
                )}
                
                {/* Video Controls Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, var(--background-color-rgba, rgba(0,0,0,0.8)))',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <IconButton onClick={handlePlayPause} sx={{ color: '#fff' }}>
                    {playing ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  
                  <IconButton onClick={handleMute} sx={{ color: '#fff' }}>
                    {muted ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                  
                  <Box sx={{ flex: 1 }} />
                  
                  {/* Capture Thumbnail Button - Show for debugging */}
                  <IconButton 
                    onClick={captureCurrentFrame} 
                    disabled={savingThumbnail || !isOwner}
                    sx={{ 
                      color: isOwner ? '#fff' : '#ff0000',
                      '&:disabled': { color: '#666' },
                      opacity: isOwner ? 1 : 0.5
                    }}
                    title={isOwner ? "Save current frame as thumbnail" : "Only video owner can capture thumbnail"}
                  >
                    {savingThumbnail ? (
                      <CircularProgress size={20} sx={{ color: '#fff' }} />
                    ) : (
                      <CameraAlt />
                    )}
                  </IconButton>
                  
                  <IconButton onClick={handleFullscreen} sx={{ color: '#fff' }}>
                    <Fullscreen />
                  </IconButton>
                </Box>
              </Box>
            </Paper>

            {/* Video Info */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" gutterBottom>
                {video.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  icon={<Visibility />}
                  label={`${formatViewCount(viewCount)} views`}
                  size="small"
                  sx={{ bgcolor: '#333', color: '#fff' }}
                />
                <Typography variant="body2" color="text.secondary">
                  {formatDistanceToNow(new Date(video.createdAt), { addSuffix: true })}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant={liked ? "contained" : "outlined"}
                  startIcon={liked ? <ThumbUp /> : <ThumbUpOutlined />}
                  onClick={handleLike}
                  sx={{ 
                    borderColor: '#666',
                    color: liked ? '#000' : '#fff',
                    bgcolor: liked ? '#FFEB3B' : 'transparent'
                  }}
                >
                  {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Share />}
                  onClick={handleShare}
                  sx={{ borderColor: '#666', color: '#fff' }}
                >
                  Share
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Comment />}
                  onClick={() => setShowComments(!showComments)}
                  sx={{ borderColor: '#666', color: '#fff' }}
                >
                  {video.commentCount || 0} Comments
                </Button>
              </Stack>

              <Divider sx={{ bgcolor: '#333', my: 2 }} />

              {/* Uploader Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  src={video.uploader?.profileImg}
                  sx={{ width: 48, height: 48, cursor: 'pointer' }}
                  onClick={() => history.push(`/artist/${video.uploader._id}`)}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => history.push(`/artist/${video.uploader._id}`)}
                  >
                    {video.uploader?.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {video.uploader?.followersCount || 0} followers
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => history.push(`/artist/${video.uploader._id}`)}
                >
                  View Profile
                </Button>
              </Box>

              {/* Description */}
              {video.description && (
                <Box sx={{ bgcolor: 'var(--background-color, #1a1a1a)', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {video.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ bgcolor: '#333', my: 2 }} />

              {/* Comments Section */}
              {showComments && (
                <VideoComments 
                  videoId={videoId} 
                  commentCount={video.commentCount || 0}
                />
              )}
            </Box>
          </Grid>

          {/* Related Videos */}
          <Grid item xs={12} lg={4}>
            <Typography variant="h6" gutterBottom>
              Related Videos
            </Typography>
            
            <Stack spacing={2}>
              {relatedVideos.map((relatedVideo) => (
                <Paper
                  key={relatedVideo._id}
                  sx={{ 
                    p: 1, 
                    bgcolor: 'var(--background-color, #1a1a1a)', 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#2a2a2a' }
                  }}
                  onClick={() => {
                    // Pause current video before navigating
                    if (videoRef.current) {
                      videoRef.current.pause();
                    }
                    history.push(`/video/${relatedVideo._id}`);
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <Box
                        sx={{
                          paddingTop: '56.25%',
                          position: 'relative',
                          bgcolor: 'var(--background-color, #000)',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={relatedVideo.thumbnailUrl}
                          alt={relatedVideo.title}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" noWrap>
                        {relatedVideo.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {relatedVideo.uploader?.fullName}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatViewCount(relatedVideo.views || 0)} views
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VideoPlayer;